import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useApi } from '../hooks/useApi';
import { toastError } from '../Toaster/Toast';

interface QuotationsCountContextType {
	quotesCount: { label: string; count: number; id?: string }[];
	refreshCounts: () => Promise<void>;
	isLoading: boolean;
}

const QuotationsCountContext = createContext<QuotationsCountContextType | undefined>(undefined);

interface QuotationsCountProviderProps {
	children: ReactNode;
}

export const QuotationsCountProvider: React.FC<QuotationsCountProviderProps> = ({ children }) => {
	const api = useApi();
	const [quotesCount, setQuotesCount] = useState<{ label: string; count: number; id?: string }[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	const refreshCounts = useCallback(async () => {
		setIsLoading(true);
		try {
			const result = await api.quotations.countQuotation();
			if (result.errored) {
				toastError({ title: 'Error', description: 'Error counting quotes. Try again later' });
			} else {
				setQuotesCount(result);
			}
		} catch (error) {
			toastError({ title: 'Error', description: 'Error counting quotes. Try again later' });
		} finally {
			setIsLoading(false);
		}
	}, [api.quotations]);

	const contextValue: QuotationsCountContextType = {
		quotesCount,
		refreshCounts,
		isLoading,
	};

	return (
		<QuotationsCountContext.Provider value={contextValue}>
			{children}
		</QuotationsCountContext.Provider>
	);
};

export const useQuotationsCount = (): QuotationsCountContextType => {
	const context = useContext(QuotationsCountContext);
	if (!context) {
		throw new Error('useQuotationsCount must be used within a QuotationsCountProvider');
	}
	return context;
};